import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Divider,
  Paper
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Info } from '@mui/icons-material';
import { designTokens } from '../../theme/designTokens';
import { createHeaders, createRows } from '../../methods';
import {
  selectSelectedRowIds,
  setSelectedRows
} from '../../features/analysis/analysisSlice';

const RecordSelectionStep = ({ queryResultData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const selectedRowIds = useSelector(selectSelectedRowIds);

  console.log({ selectedRowIds })

  const { headers, rows, count } = useMemo(() => {
    if (!queryResultData || !queryResultData.response) {
      return { headers: [], rows: [], count: 0 };
    }

    // Handle the "Retry" case
    if (queryResultData.response === "Retry") {
      return { headers: [], rows: [], count: 0 };
    }

    try {
      // The response might already be parsed or need parsing
      let response;
      if (typeof queryResultData.response === 'string') {
        response = JSON.parse(queryResultData.response);
      } else {
        response = queryResultData.response;
      }

      const headers = createHeaders(response);
      const rows = createRows(response, 1);
      const count = queryResultData.rowCount || rows.length;

      return { headers, rows, count };
    } catch (error) {
      console.error('RecordSelectionStep - Error parsing response:', error);
      return { headers: [], rows: [], count: 0 };
    }
  }, [queryResultData]);

  const handleSelectionChange = (newSelectionModel) => {
    console.log('RecordSelectionStep - Selection changed:', newSelectionModel);

    const ids = newSelectionModel.type === 'include' ? [...newSelectionModel.ids] : [];
    const selectedRecords = rows.filter(row => ids.includes(row.id));

    console.log('RecordSelectionStep - Dispatching setSelectedRows:', {
      rowIds: ids,
      records: selectedRecords
    });

    dispatch(setSelectedRows({
      rowIds: ids,
      records: selectedRecords
    }));
  };

  const isDataAvailable = rows.length > 0;
  const hasSelection = selectedRowIds.length > 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: designTokens.spacing.xs, py: designTokens.spacing.xxs }}>
        {/* Styled Info Box */}
        <Paper
          elevation={0}
          sx={{
            padding: theme.spacing(0.75, 1),
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: theme.palette.mode === 'dark'
              ? `${theme.palette.primary.main}15`
              : `${theme.palette.primary.main}08`,
            borderRadius: designTokens.borderRadius.xs,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            mb: hasSelection ? theme.spacing(1) : 0
          }}
        >
          <Info
            sx={{
              color: theme.palette.primary.main,
              fontSize: 16,
              flexShrink: 0
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: designTokens.fontWeight.semibold,
                color: theme.palette.primary.main,
                mb: 0.25,
                fontSize: designTokens.fontSize.sm
              }}
            >
              Select Records for Analysis
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: designTokens.fontSize.xs,
                lineHeight: designTokens.lineHeight.tight
              }}
            >
              Choose records to analyze with AI-powered insights. Select multiple to compare results.
            </Typography>
          </Box>
        </Paper>

        {hasSelection && (
          <Typography variant="body2" color="primary" sx={{
            fontWeight: designTokens.fontWeight.medium,
            fontSize: designTokens.fontSize.sm
          }}>
            {selectedRowIds.length} record{selectedRowIds.length !== 1 ? 's' : ''} selected
          </Typography>
        )}
      </Box>
      {/* Data Grid */}
      <Box sx={{
        flex: 1,
        px: designTokens.spacing.xs,
        overflow: 'hidden'
      }}>
        {!isDataAvailable ? (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: 2
          }}>
            <Typography variant="h6" color="text.secondary">
              No Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Please run a query first to see results available for analysis.
            </Typography>
          </Box>
        ) : (
          <DataGridPro
            sx={{
              height: '100%',
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: designTokens.borderRadius.sm,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: theme.palette.grey[50],
                minHeight: '40px !important',
                maxHeight: '40px !important',
              },
              '& .MuiDataGrid-row': {
                minHeight: '36px !important',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light + '15',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light + '25',
                  },
                },
              },
              '& .MuiDataGrid-cell': {
                padding: theme.spacing(0.5, 1),
                fontSize: designTokens.fontSize.sm,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: designTokens.fontSize.sm,
                fontWeight: designTokens.fontWeight.semibold,
              },
            }}
            rows={rows}
            columns={headers}
            rowCount={count}
            checkboxSelection
            rowSelectionModel={selectedRowIds}
            onRowSelectionModelChange={handleSelectionChange}
            disableRowSelectionOnClick={false}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}>
                  <Typography color="text.secondary">
                    No records available
                  </Typography>
                </Box>
              ),
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            density="compact"
          />
        )}
      </Box>
    </Box>
  );
};

export default RecordSelectionStep;
